import React, { useState } from 'react';
import { Button } from '../components/Button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SparklesIcon, CheckCircleIcon, ShieldCheckIcon, ClockIcon, AwardIcon, PhoneIcon, MessageSquareIcon, CalendarIcon, HelpCircleIcon, BellIcon, PlayIcon, StarIcon, QuoteIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
function BoltGradientHeroLightAnimated() {
  return <section className="w-full min-h-screen bg-[#FAFAFA] relative overflow-hidden flex items-center" data-id="element-822">
      {/* Animated Gradient Mesh Background */}
      <div className="absolute inset-0" data-id="element-823">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F1B530] rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob" data-id="element-824"></div>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#D09F2B] rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob animation-delay-2000" data-id="element-825"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-[#A78942] rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-blob animation-delay-4000" data-id="element-826"></div>
      </div>

      <div className="max-w-7xl mx-auto px-8 md:px-12 py-20 relative z-10" data-id="element-827">
        <div className="text-center mb-16" data-id="element-828">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-md border border-[#F1B530]/30 rounded-full px-6 py-3 mb-8 shadow-sm animate-[fadeInUp_0.8s_ease-out]" data-id="element-829">
            <SparklesIcon className="w-4 h-4 text-[#F1B530]" data-id="element-830" />
            <span className="font-['Inter'] text-sm text-[#1A1A1A] font-medium" data-id="element-831">
              AI-Powered Answering Service
            </span>
          </div>

          <h1 className="font-['Poppins'] font-bold text-5xl md:text-7xl lg:text-8xl text-[#1A1A1A] mb-8 leading-tight animate-[fadeInUp_0.8s_ease-out_0.2s] opacity-0 [animation-fill-mode:forwards]" data-id="element-832">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent animate-gradient" data-id="element-833">
              Never Miss
            </span>
            <br data-id="element-834" />
            Another Call
          </h1>

          <p className="font-['Inter'] text-xl md:text-2xl text-[#6B6B6B] max-w-3xl mx-auto mb-12 leading-relaxed font-light animate-[fadeInUp_0.8s_ease-out_0.4s] opacity-0 [animation-fill-mode:forwards]" data-id="element-835">
            24/7 AI receptionist that answers instantly, captures perfectly, and
            integrates seamlessly with your business.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-[fadeInUp_0.8s_ease-out_0.6s] opacity-0 [animation-fill-mode:forwards]" data-id="element-836">
            <Button size="lg" data-id="element-837">Start Free Trial</Button>
            <Button variant="secondary" size="lg" theme="light" data-id="element-838">
              Watch Demo
            </Button>
          </div>
        </div>

        {/* Floating Demo Card */}
        <div className="max-w-5xl mx-auto animate-[fadeInUp_0.8s_ease-out_0.8s] opacity-0 [animation-fill-mode:forwards]" data-id="element-839">
          <div className="bg-white/80 backdrop-blur-xl border border-[#E5E5E5] rounded-3xl p-8 shadow-2xl" data-id="element-840">
            <div className="aspect-video bg-[#F5F5F5] rounded-2xl flex items-center justify-center relative overflow-hidden" data-id="element-841">
              <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Dashboard preview" className="w-full h-full object-cover opacity-80" data-id="element-842" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-843">
                <div className="w-20 h-20 bg-[#F1B530] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 shadow-lg" data-id="element-844">
                  <PlayIcon className="w-8 h-8 text-white fill-white ml-1" data-id="element-845" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-846">{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>;
}
function BoltTrustBadgesLightAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const badges = [{
    icon: CheckCircleIcon,
    label: '99.9% Uptime',
    value: 'Guaranteed'
  }, {
    icon: ShieldCheckIcon,
    label: 'Enterprise Security',
    value: 'SOC 2 Certified'
  }, {
    icon: ClockIcon,
    label: '24/7 Support',
    value: 'Always Available'
  }, {
    icon: AwardIcon,
    label: '10,000+ Calls',
    value: 'Handled Daily'
  }];
  return <section className="w-full bg-white py-16 border-y border-[#E5E5E5]" data-id="element-847">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-848">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-849">
          {badges.map((badge, index) => {
          const Icon = badge.icon;
          return <div key={index} className="text-center" data-id="element-850">
                <div className="w-16 h-16 bg-[#F1B530]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#F1B530]/20" data-id="element-851">
                  <Icon className="w-8 h-8 text-[#F1B530]" data-id="element-852" />
                </div>
                <div className="font-['Libre_Baskerville'] text-2xl font-bold text-[#1A1A1A] mb-1" data-id="element-853">
                  {badge.label}
                </div>
                <div className="font-['Inter'] text-sm text-[#6B6B6B]" data-id="element-854">
                  {badge.value}
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltValuePropositionLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const benefits = [{
    icon: ClockIcon,
    title: '24/7 Instant Call Answering',
    description: 'Never miss a call again with round-the-clock AI coverage that picks up instantly'
  }, {
    icon: MessageSquareIcon,
    title: 'Customised Company Greeting',
    description: 'Fully branded experience that sounds like your business, not a generic service'
  }, {
    icon: PhoneIcon,
    title: 'Accurate Capture',
    description: 'Precise name, number, and message recording with intelligent transcription'
  }, {
    icon: BellIcon,
    title: 'Real-Time Call Summaries',
    description: 'Instant notifications with complete call context delivered immediately'
  }, {
    icon: HelpCircleIcon,
    title: 'FAQ Handling',
    description: 'Answer common questions with fenced, brand-safe responses automatically'
  }, {
    icon: CalendarIcon,
    title: 'Appointment Booking',
    description: 'Seamless calendar integration for automated scheduling and confirmations'
  }];
  return <section className="w-full bg-[#FAFAFA] py-28 md:py-36 relative overflow-hidden" data-id="element-855">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-856"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-857"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-858">
        <div className="text-center mb-20" data-id="element-859">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight" data-id="element-860">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-861">
              Why Choose Our AI Answering Service?
            </span>
          </h2>
          <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#6B6B6B]" data-id="element-862">
            Your Always-On Receptionist, without the overheads
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-863">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <div key={index} className="group relative bg-gradient-to-br from-white to-[#FAFAFA] rounded-2xl p-8 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-500 hover:-translate-y-2 shadow-sm hover:shadow-lg" data-id="element-864">
                {/* Gradient glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#F1B530]/0 to-[#F1B530]/0 group-hover:from-[#F1B530]/5 group-hover:to-[#D09F2B]/5 rounded-2xl transition-all duration-500" data-id="element-865"></div>

                <div className="relative" data-id="element-866">
                  {/* Icon with gradient background */}
                  <div className="w-16 h-16 bg-gradient-to-br from-[#F1B530] to-[#D09F2B] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md" data-id="element-867">
                    <Icon className="w-8 h-8 text-white" data-id="element-868" />
                  </div>

                  <h3 className="font-['Inter'] font-semibold text-xl text-[#1A1A1A] mb-4 group-hover:text-[#F1B530] transition-colors duration-300" data-id="element-869">
                    {benefit.title}
                  </h3>

                  <p className="font-['Inter'] text-[#6B6B6B] leading-relaxed font-light" data-id="element-870">
                    {benefit.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltFeatureShowcaseLight() {
  const feature1 = useScrollAnimation({
    threshold: 0.1
  });
  const feature2 = useScrollAnimation({
    threshold: 0.1
  });
  const feature3 = useScrollAnimation({
    threshold: 0.1
  });
  return <section className="w-full bg-white py-28 md:py-36 relative overflow-hidden" data-id="element-871">
      {/* Gradient accents */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-872"></div>
      <div className="absolute bottom-40 left-20 w-80 h-80 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-873"></div>

      <div className="max-w-7xl mx-auto px-8 md:px-12 relative z-10" data-id="element-874">
        {/* Feature 1 */}
        <div ref={feature1.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32 transition-all duration-1000 ${feature1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-875">
          <div className="relative group" data-id="element-876">
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1B530] to-[#D09F2B] rounded-3xl opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-500" data-id="element-877"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#E5E5E5] shadow-lg" data-id="element-878">
              <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Real-time dashboard" className="w-full h-full object-cover" data-id="element-879" />
            </div>
            <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-[#F1B530] to-[#D09F2B] rounded-2xl p-6 shadow-2xl" data-id="element-880">
              <div className="font-['Poppins'] text-4xl font-bold text-white mb-1" data-id="element-881">
                100%
              </div>
              <div className="font-['Inter'] text-sm text-white font-semibold" data-id="element-882">
                Capture Rate
              </div>
            </div>
          </div>

          <div data-id="element-883">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6 bg-gradient-to-r from-[#1A1A1A] to-[#F1B530] bg-clip-text text-transparent" data-id="element-884">
              Real-Time Dashboard & Analytics
            </h2>
            <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-885">
              Monitor every call in real-time with comprehensive analytics.
              Track call volume, response times, customer satisfaction, and
              conversion rates all in one intuitive dashboard.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-886">
              <li className="flex items-start" data-id="element-887">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-888">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-889">
                  Live call monitoring and notifications
                </span>
              </li>
              <li className="flex items-start" data-id="element-890">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-891">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-892">
                  Detailed call transcripts and recordings
                </span>
              </li>
              <li className="flex items-start" data-id="element-893">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-894">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-895">
                  Performance metrics and insights
                </span>
              </li>
            </ul>
            <Button data-id="element-896">Explore Dashboard</Button>
          </div>
        </div>

        {/* Feature 2 */}
        <div ref={feature2.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32 transition-all duration-1000 ${feature2.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-897">
          <div className="lg:order-2 relative group" data-id="element-898">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D09F2B] to-[#F1B530] rounded-3xl opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-500" data-id="element-899"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#E5E5E5] shadow-lg" data-id="element-900">
              <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80" alt="Integrations" className="w-full h-full object-cover" data-id="element-901" />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white border-2 border-[#F1B530] rounded-2xl p-6 shadow-2xl" data-id="element-902">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-903">
                50+
              </div>
              <div className="font-['Inter'] text-sm text-[#1A1A1A] font-semibold" data-id="element-904">
                Integrations
              </div>
            </div>
          </div>

          <div className="lg:order-1" data-id="element-905">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6 bg-gradient-to-r from-[#1A1A1A] to-[#F1B530] bg-clip-text text-transparent" data-id="element-906">
              Seamless CRM & Tool Integration
            </h2>
            <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-907">
              Connect with your existing tools effortlessly. Our AI integrates
              with popular CRMs, calendars, and business tools to keep your
              workflow smooth and data synchronized.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-908">
              <li className="flex items-start" data-id="element-909">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-910">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-911">
                  Salesforce, HubSpot, Zoho CRM
                </span>
              </li>
              <li className="flex items-start" data-id="element-912">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-913">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-914">
                  Google Calendar, Outlook, Calendly
                </span>
              </li>
              <li className="flex items-start" data-id="element-915">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-916">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-917">
                  Slack, Teams, Zapier, and more
                </span>
              </li>
            </ul>
            <Button data-id="element-918">View All Integrations</Button>
          </div>
        </div>

        {/* Feature 3 */}
        <div ref={feature3.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ${feature3.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-919">
          <div className="relative group" data-id="element-920">
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1B530] to-[#D09F2B] rounded-3xl opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-500" data-id="element-921"></div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#E5E5E5] shadow-lg" data-id="element-922">
              <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80" alt="AI training" className="w-full h-full object-cover" data-id="element-923" />
            </div>
            <div className="absolute -bottom-8 -right-8 bg-white border-2 border-[#F1B530] rounded-2xl p-6 shadow-2xl" data-id="element-924">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-925">
                &lt;5min
              </div>
              <div className="font-['Inter'] text-sm text-[#1A1A1A] font-semibold" data-id="element-926">
                Setup Time
              </div>
            </div>
          </div>

          <div data-id="element-927">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6 bg-gradient-to-r from-[#1A1A1A] to-[#F1B530] bg-clip-text text-transparent" data-id="element-928">
              Custom AI Training for Your Business
            </h2>
            <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-929">
              Train your AI receptionist with your business knowledge, FAQs, and
              specific protocols. The more you customize, the more natural and
              helpful your AI becomes.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-930">
              <li className="flex items-start" data-id="element-931">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-932">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-933">
                  Upload FAQs and knowledge base
                </span>
              </li>
              <li className="flex items-start" data-id="element-934">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-935">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-936">
                  Define custom call routing rules
                </span>
              </li>
              <li className="flex items-start" data-id="element-937">
                <span className="text-[#F1B530] mr-3 text-xl" data-id="element-938">•</span>
                <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-939">
                  Set brand voice and tone guidelines
                </span>
              </li>
            </ul>
            <Button data-id="element-940">Start Customizing</Button>
          </div>
        </div>
      </div>
    </section>;
}
function BoltPricingPlansLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const plans = [{
    name: 'Scout 25',
    price: 'AUD $65',
    calls: '25 calls/month',
    minutes: '125',
    target: 'Perfect for solo operators testing the waters',
    inclusions: ['Instant call pickup', 'Customised company greeting', 'Name, number & message capture', 'Real-time call summaries via SMS/email', 'Call recordings & transcripts', 'Dashboard analytics', 'Excess: $2.60/call or $0.52/min'],
    ctaText: 'Start 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Forager 50',
    price: 'AUD $115',
    calls: '50 calls/month',
    minutes: '250',
    target: 'Small teams with steady enquiry flow',
    inclusions: ['Everything in Scout 25', 'Priority support', 'Extended call handling', 'Multi-user dashboard access', 'Custom call routing rules', 'Excess: $2.30/call or $0.46/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording',
    isPopular: true
  }, {
    name: 'Drift 75',
    price: 'AUD $165',
    calls: '75 calls/month',
    minutes: '375',
    target: 'Growing teams with rising call peaks',
    inclusions: ['Everything in Forager 50', 'Advanced analytics', 'Team collaboration tools', 'Custom integrations', 'Dedicated account manager', 'Excess: $2.20/call or $0.44/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Surge 150',
    price: 'AUD $325',
    calls: '150 calls/month',
    minutes: '750',
    target: 'High-tempo operations with regular spikes',
    inclusions: ['Everything in Drift 75', 'Priority call routing', 'Advanced workflow automation', 'Custom reporting', 'API access', 'Excess: $2.17/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Swarm 300',
    price: 'AUD $645',
    calls: '300 calls/month',
    minutes: '1,500',
    target: 'Heavy daily call activity teams',
    inclusions: ['Everything in Surge 150', 'Enterprise-grade security', 'Custom AI training', 'White-label options', 'SLA guarantees', 'Excess: $2.15/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Colony 500',
    price: 'AUD $955',
    calls: '500 calls/month',
    minutes: '2,250',
    target: 'Enterprise-level high-volume operations',
    inclusions: ['Everything in Swarm 300', 'Dedicated infrastructure', 'Custom AI models', 'Advanced compliance features', '24/7 premium support', 'Excess: $1.91/call or $0.42/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'AI Automation Customisation',
    target: 'For businesses requiring advanced automation and integrations beyond standard plans',
    inclusions: ['Custom call volumes', 'Bespoke AI training', 'Enterprise integrations', 'Advanced workflow automation', 'Dedicated success team', 'Custom SLAs'],
    ctaText: 'Contact Sales',
    isCustom: true
  }];
  return <section className="w-full bg-[#FAFAFA] py-28 md:py-36 relative overflow-hidden" data-id="element-941">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-942"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-943"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-944">
        <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-center mb-20 tracking-tight" data-id="element-945">
          <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-946">
            Plans
          </span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-947">
          {plans.slice(0, 6).map((plan, index) => <div key={index} className={`bg-white rounded-2xl p-10 border transition-all duration-300 hover:-translate-y-1 relative flex flex-col h-full shadow-sm hover:shadow-lg ${plan.isPopular ? 'border-[#F1B530] shadow-[0_0_40px_rgba(241,181,48,0.15)]' : 'border-[#E5E5E5] hover:border-[#F1B530]/50'}`} data-id="element-948">
              {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-white px-6 py-2 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-949">
                  Most Popular
                </div>}

              <div className="mb-8" data-id="element-950">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-951">
                  {plan.name}
                </h3>
                {!plan.isCustom ? <>
                    <div className="mb-3" data-id="element-952">
                      <span className="font-['Poppins'] font-bold text-5xl text-[#1A1A1A]" data-id="element-953">
                        {plan.price}
                      </span>
                      <span className="text-[#6B6B6B] font-['Inter'] ml-2 text-sm" data-id="element-954">
                        plus GST/month
                      </span>
                    </div>
                    <p className="text-[#6B6B6B] font-['Inter'] text-sm mb-2 font-light" data-id="element-955">
                      Up to {plan.calls} • {plan.minutes} free minutes
                    </p>
                  </> : <div className="mb-3" data-id="element-956">
                    <span className="font-['Poppins'] font-bold text-4xl text-[#1A1A1A]" data-id="element-957">
                      Custom Pricing
                    </span>
                  </div>}
                <p className="text-[#6B6B6B] font-['Inter'] text-sm italic font-light" data-id="element-958">
                  {plan.target}
                </p>
              </div>

              <div className="mb-10 flex-grow" data-id="element-959">
                <ul className="space-y-4" data-id="element-960">
                  {plan.inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-961">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-962" />
                      <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-963">
                        {inclusion}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-964">
                <Button className="w-full" size="lg" data-id="element-965">
                  {plan.ctaText}
                </Button>

                {plan.ctaLink && <a href="#" className="block text-center mt-5 text-[#6B6B6B] font-['Inter'] text-sm hover:text-[#F1B530] transition-colors duration-300" data-id="element-966">
                    {plan.ctaLink}
                  </a>}
              </div>
            </div>)}
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-967">
          <div className="bg-white rounded-2xl p-10 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full shadow-sm hover:shadow-lg" data-id="element-968">
            <div className="mb-8" data-id="element-969">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-970">
                {plans[6].name}
              </h3>
              <div className="mb-3" data-id="element-971">
                <span className="font-['Poppins'] font-bold text-4xl text-[#1A1A1A]" data-id="element-972">
                  Custom Pricing
                </span>
              </div>
              <p className="text-[#6B6B6B] font-['Inter'] text-sm italic font-light" data-id="element-973">
                {plans[6].target}
              </p>
            </div>

            <div className="mb-10 flex-grow" data-id="element-974">
              <ul className="space-y-4" data-id="element-975">
                {plans[6].inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-976">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-977" />
                    <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-978">
                      {inclusion}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-auto" data-id="element-979">
              <Button className="w-full" size="lg" data-id="element-980">
                {plans[6].ctaText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function BoltTestimonialsLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const testimonials = [{
    quote: "This AI answering service has transformed how we handle customer calls. We've seen a 40% increase in captured leads since implementing it.",
    author: 'Sarah Mitchell',
    role: 'CEO, TechStart Solutions',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'The 24/7 coverage means we never miss an opportunity. Our customers love the instant response, and so do we.',
    author: 'James Chen',
    role: 'Operations Director, GrowthCo',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: "Setup was incredibly easy, and the AI sounds so natural. It's like having a professional receptionist without the overhead costs.",
    author: 'Emma Thompson',
    role: 'Founder, Bright Ideas Agency',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-white py-28 md:py-36 relative overflow-hidden" data-id="element-981">
      {/* Gradient accents */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-982"></div>
      <div className="absolute bottom-40 right-20 w-80 h-80 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-983"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-984">
        <div className="text-center mb-20" data-id="element-985">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl mb-6" data-id="element-986">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-987">
              Trusted by Growing Businesses
            </span>
          </h2>
          <p className="font-['Inter'] text-xl text-[#6B6B6B] font-light" data-id="element-988">
            See what our customers have to say
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-989">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white/80 backdrop-blur-sm border border-[#E5E5E5] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md" data-id="element-990">
              <div className="flex gap-1 mb-6" data-id="element-991">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-992" />)}
              </div>

              <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-993" />

              <p className="font-['Inter'] text-[#1A1A1A] mb-8 leading-relaxed font-light" data-id="element-994">
                "{testimonial.quote}"
              </p>

              <div className="flex items-center gap-4" data-id="element-995">
                <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-996" />
                <div data-id="element-997">
                  <div className="font-['Inter'] font-semibold text-[#1A1A1A]" data-id="element-998">
                    {testimonial.author}
                  </div>
                  <div className="font-['Inter'] text-sm text-[#6B6B6B]" data-id="element-999">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function BoltAddOnsLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const addOns = [{
    icon: PhoneForwardedIcon,
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant Escalation for Time Sensitive Opportunities', 'Transfers With Full Caller Context', 'Smart Routing Based on Your Real Business Rules']
  }, {
    icon: MessageCircleIcon,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Turn AI agent into trained front-line knowledge assistant', 'Fenced responses using only approved information', 'Clear brand-safe responses', 'Faster resolution & reduced message volume']
  }, {
    icon: CalendarCheckIcon,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80',
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Real-time calendar syncing (Google, Outlook, etc.)', 'Automated booking and confirmation', 'Custom booking rules', 'Caller notifications and internal alerts']
  }];
  return <section className="w-full bg-[#FAFAFA] py-28 md:py-36 relative overflow-hidden" data-id="element-1000">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-1001"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-1002"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-1003">
        <div className="text-center mb-20" data-id="element-1004">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl mb-6 tracking-tight" data-id="element-1005">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-1006">
              Universal Add-Ons
            </span>
          </h2>
          <p className="font-['Inter'] text-lg text-[#6B6B6B] max-w-3xl mx-auto font-light" data-id="element-1007">
            Available for all plans to extend your AI answering service
            capabilities
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1008">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <div key={index} className="bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md" data-id="element-1009">
                <div className="relative h-48 overflow-hidden" data-id="element-1010">
                  <img src={addOn.image} alt={addOn.name} className="w-full h-full object-cover opacity-80" data-id="element-1011" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" data-id="element-1012"></div>
                  <div className="absolute bottom-4 left-4 w-14 h-14 bg-gradient-to-br from-[#F1B530] to-[#D09F2B] backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md" data-id="element-1013">
                    <Icon className="w-7 h-7 text-white" data-id="element-1014" />
                  </div>
                </div>

                <div className="p-10" data-id="element-1015">
                  <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-1016">
                    {addOn.name}
                  </h3>

                  <div className="mb-8" data-id="element-1017">
                    <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-1018">
                      {addOn.price}
                    </span>
                    <span className="text-[#6B6B6B] font-['Inter'] text-sm ml-2" data-id="element-1019">
                      {addOn.setup}
                    </span>
                  </div>

                  <ul className="space-y-4" data-id="element-1020">
                    {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-1021">
                        <span className="text-[#F1B530] mr-3 flex-shrink-0 text-lg" data-id="element-1022">
                          •
                        </span>
                        <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-1023">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function BoltAdvancedAutomationLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Route leads intelligently'
  }, {
    icon: ZapIcon,
    text: 'Trigger automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Sync directly into CRMs'
  }, {
    icon: BrainCircuitIcon,
    text: 'Collect structured information'
  }];
  return <section className="w-full bg-white py-28 md:py-36 relative overflow-hidden" data-id="element-1024">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-1025"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-1026"></div>

      <div ref={ref} className={`max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-1027">
        <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight" data-id="element-1028">
          <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-1029">
            AI Automation Customisation Advanced
          </span>
        </h2>
        <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#6B6B6B] mb-10" data-id="element-1030">
          Automate More Than Calls. Automate Your Entire Frontline.
        </p>

        <p className="font-['Inter'] text-lg text-[#6B6B6B] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-1031">
          For businesses handling 500+ calls per month, unlock advanced
          capabilities that transform your AI answering service into a complete
          frontline automation platform.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-1032">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 shadow-sm hover:shadow-md h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-1033">
                <Icon className="w-10 h-10 text-[#F1B530] mx-auto mb-5" data-id="element-1034" />
                <p className="font-['Inter'] text-[#1A1A1A] font-light text-center" data-id="element-1035">
                  {capability.text}
                </p>
              </div>;
        })}
        </div>

        <div className="mb-16 relative" data-id="element-1036">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#E5E5E5] max-w-4xl mx-auto shadow-lg" data-id="element-1037">
            <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80" alt="Automation workflow visualization" className="w-full h-64 object-cover rounded-xl opacity-90" data-id="element-1038" />
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-1039">
              <div className="bg-[#F1B530]/10 backdrop-blur-md border border-[#F1B530]/30 rounded-xl px-8 py-4" data-id="element-1040">
                <p className="font-['Inter'] text-[#F1B530] font-semibold text-lg" data-id="element-1041">
                  Complete Workflow Automation
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-10 border border-[#E5E5E5] mb-12 max-w-3xl mx-auto shadow-sm" data-id="element-1042">
          <h3 className="font-['Inter'] font-semibold text-xl mb-6 text-[#1A1A1A]" data-id="element-1043">
            Extended Capabilities Include:
          </h3>
          <ul className="font-['Inter'] text-[#6B6B6B] space-y-3 text-left max-w-2xl mx-auto font-light" data-id="element-1044">
            <li data-id="element-1045">• Support multi-step decision trees</li>
            <li data-id="element-1046">• Extended conversational capabilities</li>
            <li data-id="element-1047">• Custom API integrations</li>
            <li data-id="element-1048">• Advanced data collection and processing</li>
            <li data-id="element-1049">• Bespoke workflow automation</li>
          </ul>
        </div>

        <Button size="lg" data-id="element-1050">Contact Sales</Button>
      </div>
    </section>;
}
function BoltIndustryBenefitsLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#FAFAFA] py-28 relative overflow-hidden" data-id="element-1051">
      {/* Gradient accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-1052"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-1053"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-1054">
        <div className="text-center mb-16" data-id="element-1055">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl mb-6" data-id="element-1056">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-1057">
              Trusted Across Industries
            </span>
          </h2>
          <p className="font-['Inter'] text-xl text-[#6B6B6B] font-light" data-id="element-1058">
            Tailored solutions for every business type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1059">
          {industries.map((industry, index) => <div key={index} className="group relative bg-gradient-to-br from-white to-[#FAFAFA] rounded-2xl p-8 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-500 shadow-sm hover:shadow-md" data-id="element-1060">
              <div className="absolute inset-0 bg-gradient-to-br from-[#F1B530]/0 to-[#F1B530]/0 group-hover:from-[#F1B530]/5 group-hover:to-[#D09F2B]/5 rounded-2xl transition-all duration-500" data-id="element-1061"></div>

              <div className="relative" data-id="element-1062">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-6 group-hover:text-[#F1B530] transition-colors duration-300" data-id="element-1063">
                  {industry.name}
                </h3>
                <ul className="space-y-3" data-id="element-1064">
                  {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#6B6B6B] flex items-start" data-id="element-1065">
                      <span className="text-[#F1B530] mr-2" data-id="element-1066">•</span>
                      {benefit}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function BoltFAQLight() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-white py-28 md:py-36 relative overflow-hidden" data-id="element-1067">
      {/* Gradient accents */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#F1B530] opacity-5 rounded-full blur-3xl" data-id="element-1068"></div>
      <div className="absolute bottom-40 right-20 w-80 h-80 bg-[#D09F2B] opacity-5 rounded-full blur-3xl" data-id="element-1069"></div>

      <div ref={ref} className={`max-w-4xl mx-auto px-8 md:px-12 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-1070">
        <div className="text-center mb-16" data-id="element-1071">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl mb-6" data-id="element-1072">
            <span className="bg-gradient-to-r from-[#1A1A1A] via-[#F1B530] to-[#1A1A1A] bg-clip-text text-transparent" data-id="element-1073">
              Frequently Asked Questions
            </span>
          </h2>
          <p className="font-['Inter'] text-lg text-[#6B6B6B] font-light" data-id="element-1074">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-1075">
          {faqs.map((faq, index) => <div key={index} className="bg-white/80 backdrop-blur-sm border border-[#E5E5E5] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/50 shadow-sm hover:shadow-md" data-id="element-1076">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-1077">
                <span className="font-['Poppins'] font-semibold text-xl text-[#1A1A1A] pr-8" data-id="element-1078">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-1079" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-1080">
                <div className="px-8 pb-6" data-id="element-1081">
                  <p className="font-['Inter'] text-[#6B6B6B] leading-relaxed font-light" data-id="element-1082">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
export function BoltInspiredLight() {
  return <div className="w-full min-h-screen bg-[#FAFAFA]" data-id="element-1083">
      <BoltGradientHeroLightAnimated data-id="element-1084" />
      <BoltTrustBadgesLightAnimated data-id="element-1085" />
      <BoltValuePropositionLight data-id="element-1086" />
      <BoltFeatureShowcaseLight data-id="element-1087" />
      <BoltIndustryBenefitsLight data-id="element-1088" />
      <BoltPricingPlansLight data-id="element-1089" />
      <BoltTestimonialsLight data-id="element-1090" />
      <BoltAddOnsLight data-id="element-1091" />
      <BoltAdvancedAutomationLight data-id="element-1092" />
      <BoltFAQLight data-id="element-1093" />
    </div>;
}